import { CalendarClock, CheckCircle2, Clock, CreditCard, Play, UserX, XCircle } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';
import { APPOINTMENT_TRANSITIONS, STATUS_LABELS, type AppointmentStatus, type WaitlistMatch } from '@barbeiropro/contracts';
import { ErrorState, LoadingState } from '@/components/shared/states';
import { StatusBadge } from '@/components/shared/status-badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { errorMessage } from '@/lib/api';
import { fmtDateTime, fmtTime, maskPhone, money } from '@/lib/format';
import { useTimezone } from '@/lib/org';
import { useSession } from '@/lib/session';
import { emailNote, useAppointment, useStatusMutation, type AppointmentDetail } from './api';
import { ReasonModal } from './reason-modal';
import { RescheduleModal } from './reschedule-modal';

const SOURCE: Record<string, string> = { internal: 'Recepção', public: 'Site', ai_suggested: 'Assistente' };

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[6.5rem_1fr] gap-2 text-sm">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0 break-words">{children}</dd>
    </div>
  );
}

export function AppointmentPanel({
  id,
  onClose,
  onOfferWaitlist,
}: {
  id: string | null;
  onClose: () => void;
  onOfferWaitlist: (match: WaitlistMatch, appt: AppointmentDetail) => void;
}) {
  const tz = useTimezone();
  const { can } = useSession();
  const q = useAppointment(id);
  const mutate = useStatusMutation(id ?? '');
  const [reschedule, setReschedule] = useState(false);
  const [cancel, setCancel] = useState(false);
  const [noShow, setNoShow] = useState(false);
  const [matches, setMatches] = useState<WaitlistMatch[]>([]);
  const a = q.data;
  const canWrite = can('agenda.write');

  const run = async (status: AppointmentStatus, reason?: string, okMsg?: string) => {
    try {
      const r = await mutate.mutateAsync({ status, reason: reason || undefined });
      toast.success(okMsg ?? `Agendamento: ${STATUS_LABELS[status].toLowerCase()}`);
      const note = emailNote(r.emailStatus);
      if (note) toast.message(note);
      setMatches(r.waitlistMatches ?? []);
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const next = a ? APPOINTMENT_TRANSITIONS[a.status] : [];

  return (
    <Sheet open={Boolean(id)} onOpenChange={(v) => !v && (setMatches([]), onClose())}>
      <SheetContent className="flex w-full flex-col gap-0 p-0 sm:max-w-md">
        <SheetHeader className="shrink-0 border-b px-5 py-4 text-left">
          <SheetTitle>{a ? a.customerName : 'Agendamento'}</SheetTitle>
          <SheetDescription>{a ? `${fmtDateTime(a.startsAt, tz)} · ${a.durationMinutes} min` : 'Detalhes do agendamento'}</SheetDescription>
        </SheetHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {q.isLoading && <LoadingState />}
          {q.isError && <ErrorState error={q.error} onRetry={() => q.refetch()} />}
          {a && (
            <div className="grid gap-5">
              <div className="flex items-center gap-2">
                <StatusBadge status={a.status} />
                <span className="text-xs text-muted-foreground">Origem: {SOURCE[a.source] ?? a.source}</span>
              </div>

              <dl className="grid gap-2">
                <Row label="Serviço">{a.serviceName}</Row>
                <Row label="Profissional">
                  <span className="inline-flex items-center gap-1.5">
                    <span className="size-2.5 rounded-full" style={{ background: a.barberColor }} aria-hidden /> {a.barberName}
                  </span>
                </Row>
                <Row label="Unidade">{a.branchName}</Row>
                <Row label="Horário">
                  <span className="tabular">
                    {fmtTime(a.startsAt, tz)} – {fmtTime(a.endsAt, tz)}
                  </span>
                </Row>
                <Row label="Valor">
                  <span className="tabular">{money(a.priceCents)}</span>
                </Row>
                <Row label="Telefone">{a.customerPhone ? <a className="underline-offset-2 hover:underline" href={`tel:${a.customerPhone}`}>{maskPhone(a.customerPhone)}</a> : '—'}</Row>
                <Row label="E-mail">{a.customerEmail ?? <span className="text-muted-foreground">Sem e-mail: não recebe avisos</span>}</Row>
                {a.notes && <Row label="Observações">{a.notes}</Row>}
                {a.cancellationReason && <Row label="Motivo">{a.cancellationReason}</Row>}
              </dl>

              {matches.length > 0 && (
                <div className="grid gap-2 rounded-md border border-brass/50 bg-brass/10 p-3">
                  <p className="text-sm font-medium">Na lista de espera para este horário</p>
                  {matches.map((m) => (
                    <div key={m.id} className="flex items-center justify-between gap-2 text-sm">
                      <span className="min-w-0 truncate">
                        {m.customerName}
                        {m.customerPhone && <span className="text-muted-foreground"> · {maskPhone(m.customerPhone)}</span>}
                      </span>
                      <Button size="xs" onClick={() => onOfferWaitlist(m, a)}>
                        Agendar
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {canWrite && (
                <div className="flex flex-wrap gap-2">
                  {next.includes('confirmed') && (
                    <Button size="sm" disabled={mutate.isPending} onClick={() => run('confirmed', undefined, 'Agendamento confirmado')}>
                      <CheckCircle2 /> Confirmar
                    </Button>
                  )}
                  {next.includes('in_progress') && (
                    <Button size="sm" disabled={mutate.isPending} onClick={() => run('in_progress', undefined, 'Atendimento iniciado')}>
                      <Play /> Iniciar atendimento
                    </Button>
                  )}
                  {next.includes('completed') && (
                    <Button size="sm" disabled={mutate.isPending} onClick={() => run('completed', undefined, 'Atendimento concluído')}>
                      <CheckCircle2 /> Concluir
                    </Button>
                  )}
                  {a.status === 'completed' && can('cash.operate') && (
                    <Button size="sm" asChild>
                      <Link to={`/app/caixa?receber=${a.id}`}>
                        <CreditCard /> Receber pagamento
                      </Link>
                    </Button>
                  )}
                  {(a.status === 'requested' || a.status === 'confirmed') && (
                    <Button size="sm" variant="outline" onClick={() => setReschedule(true)}>
                      <CalendarClock /> Reagendar
                    </Button>
                  )}
                  {next.includes('no_show') && (
                    <Button size="sm" variant="outline" onClick={() => setNoShow(true)}>
                      <UserX /> Ausência
                    </Button>
                  )}
                  {next.includes('cancelled') && (
                    <Button size="sm" variant="outline" className="text-destructive" onClick={() => setCancel(true)}>
                      <XCircle /> Cancelar
                    </Button>
                  )}
                </div>
              )}

              <Separator />
              <section aria-labelledby="hist-title" className="grid gap-3">
                <h3 id="hist-title" className="flex items-center gap-1.5 text-sm font-medium">
                  <Clock className="size-4" aria-hidden /> Histórico
                </h3>
                <ol className="grid gap-3 border-l pl-4">
                  {a.history.map((h) => (
                    <li key={h.id} className="relative text-sm">
                      <span className="absolute -left-[1.3rem] top-1.5 size-2 rounded-full bg-primary" aria-hidden />
                      <p className="font-medium">
                        {h.event === 'created' && `Criado como ${STATUS_LABELS[h.toStatus].toLowerCase()}`}
                        {h.event === 'status_change' && `${h.fromStatus ? STATUS_LABELS[h.fromStatus] : ''} para ${STATUS_LABELS[h.toStatus].toLowerCase()}`}
                        {h.event === 'rescheduled' && `Reagendado${h.previousStartsAt ? ` (era ${fmtDateTime(h.previousStartsAt, tz)})` : ''}`}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {fmtDateTime(h.createdAt, tz)} · {h.actorKind === 'customer' ? 'Cliente' : (h.actorName ?? 'Sistema')}
                        {h.reason ? ` · ${h.reason}` : ''}
                      </p>
                    </li>
                  ))}
                </ol>
              </section>
            </div>
          )}
        </div>
      </SheetContent>

      {a && (
        <>
          <RescheduleModal appt={a} open={reschedule} onOpenChange={setReschedule} />
          <ReasonModal
            open={cancel}
            onOpenChange={setCancel}
            title="Cancelar agendamento"
            description={`${a.customerName} · ${fmtDateTime(a.startsAt, tz)}`}
            confirmTitle="Cancelar este agendamento?"
            confirmDescription="O horário volta a ficar livre e o cliente será avisado por e-mail, se tiver e-mail cadastrado."
            confirmLabel="Cancelar agendamento"
            suggestions={['Cliente desistiu', 'Cliente pediu para cancelar', 'Profissional indisponível']}
            onConfirm={(reason) => run('cancelled', reason, 'Agendamento cancelado')}
          />
          <ReasonModal
            open={noShow}
            onOpenChange={setNoShow}
            title="Registrar ausência"
            description={`${a.customerName} não compareceu.`}
            confirmTitle="Registrar ausência do cliente?"
            confirmDescription="O horário ficará marcado como ausência. Só é possível depois do início do horário mais a tolerância da política."
            confirmLabel="Registrar ausência"
            onConfirm={(reason) => run('no_show', reason, 'Ausência registrada')}
          />
        </>
      )}
    </Sheet>
  );
}

